<script lang="ts" setup>
import { tv } from 'tailwind-variants'
import { imageCarouselGalleryStyles } from './galleryStyles'

const theme = useRuntimeConfig().public
  .theme as keyof typeof imageCarouselGalleryStyles

const styling = tv({
  extend: imageCarouselGalleryStyles[theme],
})

const { buttonIcon, buttonStyling, borderRadius } = styling()

interface Props {
  slides?: { image?: string; alt?: string }[]
  rounded?: boolean
}

defineProps<Props>()

defineEmits(['toggle'])
</script>

<template>
  <div class="hidden lg:grid grid-cols-10 grid-rows-8 h-full gap-sm">
    <div
      class="col-span-7 row-span-6 overflow-hidden relative"
      :class="rounded ? 'rounded-xl' : borderRadius()"
    >
      <ClientOnly>
        <template #fallback>
          <Skeleton class="h-full w-full" />
        </template>
        <div
          class="bg-brand-secondary-800/80 absolute bottom-8 right-8 rounded h-[37px] w-[37px] flex justify-center items-center cursor-pointer"
          @click="$emit('toggle', 0)"
        >
          <Icon
            class="text-brand-white"
            name="material-symbols:expand-content-rounded"
            size="35"
          />
        </div>

        <NuxtImg
          class="object-cover h-full w-full cursor-pointer"
          sizes="lg:900px"
          provider="twenty"
          format="webp"
          :data-src="slides[0].image"
          :src="slides[0]?.image"
          @click="$emit('toggle', 0)"
        />
      </ClientOnly>
    </div>
    <div
      v-for="(item, index) in slides?.slice(1, 3)"
      :key="index"
      class="col-span-3 row-span-3 overflow-hidden"
      :class="rounded ? 'rounded-xl' : borderRadius()"
    >
      <ClientOnly>
        <template #fallback>
          <Skeleton class="h-full w-full" />
        </template>
        <NuxtImg
          class="object-cover select-none h-full w-full cursor-pointer"
          sizes="lg:400px"
          provider="twenty"
          format="webp"
          :data-src="item.image"
          :src="item.image"
          @click="$emit('toggle', index + 1)"
        />
      </ClientOnly>
    </div>
    <div
      v-for="(item, index) in slides?.slice(3, 8)"
      :key="index"
      class="col-span-2 row-span-2 overflow-hidden relative"
      :class="rounded ? 'rounded-xl' : borderRadius()"
    >
      <ClientOnly>
        <template #fallback>
          <Skeleton class="h-full w-full" />
        </template>
        <div
          v-if="index === 4"
          class="bg-brand-secondary-800/50 absolute w-full h-full cursor-pointer"
          @click="$emit('toggle', 0)"
        >
          <div
            :class="buttonStyling()"
            class="mx-auto mt-9 w-fit h-fit px-2 py-1 border-2 border-brand-secondary-400 z-[1000] hover:bg-brand-secondary-200 active:bg-brand-secondary-300 rounded-md font-p14 text-brand-secondary-500 items-center flex gap-1 truncate"
          >
            <Icon :name="buttonIcon()" size="16" />
            {{ $t('imageCarousel.view-gallery') }}
          </div>
        </div>
        <NuxtImg
          :key="index"
          class="object-cover select-none h-full w-full cursor-pointer"
          sizes="lg:300px"
          provider="twenty"
          format="webp"
          :data-src="item.image"
          :src="item.image"
          @click="$emit('toggle', index + 3)"
        />
      </ClientOnly>
    </div>
  </div>
</template>
